import React, { useState } from 'react';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import { useAppContext } from '../../../context/AppContext';
import { Button } from '../../../components/ui/Button';
import SafeIcon from '../../../common/SafeIcon';

export default function ManageProducts() {
  const { products, createProduct, updateProduct, deleteProduct, uploadProductImage } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    type: 'card',
    image_url: '',
    description: '',
    stripe_checkout_url: '',
    is_customizable: true
  });
  const [isUploading, setIsUploading] = useState(false);

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        ...product,
        stripe_checkout_url: product.stripe_checkout_url || '',
        is_customizable: product.is_customizable !== false
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        price: '',
        type: 'card',
        image_url: '',
        description: '',
        stripe_checkout_url: '',
        is_customizable: true
      });
    }
    setIsModalOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    const { url, error } = await uploadProductImage(file);
    setIsUploading(false);
    if (error) alert("Upload failed: " + error.message);
    else setFormData(prev => ({ ...prev, image_url: url }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, formData);
      } else {
        await createProduct(formData);
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error("Submit error:", err);
      alert("An error occurred while saving the product.");
    }
  };

  return (
    <DashboardLayout>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
          <p className="text-gray-500 mt-1">Manage Stripe links and customization settings.</p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <SafeIcon name="Plus" className="mr-2" /> Add Product
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="pb-4 font-semibold text-gray-600">Product</th>
              <th className="pb-4 font-semibold text-gray-600">Price</th>
              <th className="pb-4 font-semibold text-gray-600">Settings</th>
              <th className="pb-4 font-semibold text-gray-600 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {products.map((product) => (
              <tr key={product.id} className="group">
                <td className="py-4">
                  <div className="flex items-center gap-3">
                    <img src={product.image_url} className="w-10 h-10 rounded-lg object-cover" alt="" />
                    <div>
                      <span className="font-medium text-gray-900 block">{product.name}</span>
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider">{product.type}</span>
                    </div>
                  </div>
                </td>
                <td className="py-4 font-bold text-gray-900">${product.price}</td>
                <td className="py-4">
                  <div className="flex flex-col gap-1">
                    <span className={`text-[10px] font-bold uppercase ${product.stripe_checkout_url ? 'text-green-600' : 'text-gray-400'}`}>
                      {product.stripe_checkout_url ? 'Stripe Connected' : 'No Stripe Link'}
                    </span>
                    <span className={`text-[10px] font-bold uppercase ${product.is_customizable ? 'text-indigo-600' : 'text-orange-600'}`}>
                      {product.is_customizable ? 'Customizable' : 'Fixed Product'}
                    </span>
                  </div>
                </td>
                <td className="py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => handleOpenModal(product)} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                      <SafeIcon name="Edit2" />
                    </button>
                    <button onClick={() => { if (confirm('Delete product?')) deleteProduct(product.id) }} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <SafeIcon name="Trash2" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold mb-6">{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                  <input required type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 border rounded-xl" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                  <input required type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className="w-full px-4 py-2 border rounded-xl" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full px-4 py-2 border rounded-xl">
                    <option value="card">Card</option>
                    <option value="keychain">Keychain</option>
                    <option value="sticker">Sticker</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stripe Checkout URL</label>
                <input type="url" placeholder="https://buy.stripe.com/..." value={formData.stripe_checkout_url} onChange={(e) => setFormData({ ...formData, stripe_checkout_url: e.target.value })} className="w-full px-4 py-2 border rounded-xl text-sm" />
                <p className="text-[10px] text-gray-400 mt-1">Leave empty to use local checkout only.</p>
              </div>

              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                <input type="checkbox" id="is_customizable" checked={formData.is_customizable} onChange={(e) => setFormData({ ...formData, is_customizable: e.target.checked })} className="w-5 h-5 rounded text-indigo-600" />
                <label htmlFor="is_customizable" className="text-sm font-medium text-gray-700 cursor-pointer">Allow Customization (Name/Color)</label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Photo</label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-gray-100 rounded-xl overflow-hidden border">
                    {formData.image_url ? <img src={formData.image_url} className="w-full h-full object-cover" alt="" /> : <div className="w-full h-full flex items-center justify-center text-gray-400"><SafeIcon name="Image" size={24} /></div>}
                  </div>
                  <div className="flex-1">
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id="product-photo-upload" />
                    <label htmlFor="product-photo-upload" className="cursor-pointer inline-flex items-center px-4 py-2 bg-indigo-50 text-indigo-700 rounded-xl text-sm font-bold hover:bg-indigo-100 transition-colors">
                      <SafeIcon name="Upload" className="mr-2" /> {isUploading ? 'Uploading...' : 'Choose Photo'}
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea rows={2} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full px-4 py-2 border rounded-xl text-sm" />
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="submit" className="flex-1" disabled={isUploading}>{editingProduct ? 'Update Product' : 'Create Product'}</Button>
                <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
